/* eslint-disable indent */
import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, message, Spin, Button, Input } from "antd";

import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent,
    RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList, getAllCPList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadShipmentInscannedVsWeightAuditedReport } from "../../../../utils/downloadUtils";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";

const ShipmentInscannedVsWeightAuditedAdherenceReport = () => {
    // Set default date range to last 7 days
    const defaultDateRange = [
        moment().subtract(1, "days"),
        moment()
    ];

    const [loading, setLoading] = useState(false);
    const [hubListLoading, setHubListLoading] = useState(false);
    const [cpListLoading, setCpListLoading] = useState(false);
    const [dateRange, setDateRange] = useState(defaultDateRange);
    const [hubList, setHubList] = useState([]);

    // Filters
    const [bookingCpList, setBookingCpList] = useState([]);
    const [selectedBookingCps, setSelectedBookingCps] = useState([]);
    const [selectedInscannedHubs, setSelectedInscannedHubs] = useState([]);
    const [awbNumbers, setAwbNumbers] = useState("");
    const [headerHubList, setHeaderHubList] = useState([]);
    const [hubUser, setHubUser] = useState();


    // Fetch hubs from header/localStorage
    useEffect(() => {
        try {
            const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
            const userHubs = userFullData?.userInfo?.hubDetails || [];
            if (userHubs?.length > 0) {
                const headerHubs = [
                    { label: "All", value: "ALL" },
                    ...userHubs.map(hub => ({
                        label: hub.hubName,
                        value: hub.hubId
                    }))
                ];
                setHeaderHubList(headerHubs);
            } else {
                // If no hubs assigned, show all available hubs but without "All" option
                setHeaderHubList([]);
            }
        } catch (e) {
            console.error("Error parsing user hubs for Shipment Inscanned Vs Weight Audited Report", e);
            setHeaderHubList([]);
        }
    }, []);

    const handleMultiSelectChange = (values, setter, options = []) => {
        if (values.includes("ALL")) {
            const isLatestAll = values[values.length - 1] === "ALL";
            if (isLatestAll) {
                // Select all actual values (excluding the "ALL" option itself)
                const allActualValues = options
                    .filter(opt => opt.value !== "ALL")
                    .map(opt => opt.value);
                setter(allActualValues);
            } else {
                // If it wasn't the latest click, just filter it out (though shouldn't happen with this logic)
                setter(values.filter(v => v !== "ALL"));
            }
        } else {
            setter(values);
        }
    };

    // Ref to track whether we already set the hub for the current login session
    const hubInitializedRef = useRef(false);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });
    const [reportData, setReportData] = useState([]);
    const [lastsynced, setLastsynced] = useState(null);
    const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);

    // Request cancellation hook
    const { createNewRequest, isLatestRequest } = useRequestCancellation();

    // Sync with App Header hub changes
    useEffect(() => {
        const handleHubChanged = () => {
            const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
            const newActiveHubId = activeHub?.hubId;
            const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
            const role = user?.roles?.[0];
            const isSpecialRole = role === "ROLE_HOADMIN" || role === "ROLE_CENTRALOPERATOR";

            if (newActiveHubId && newActiveHubId !== "ALL_HUBS") {
                setSelectedInscannedHubs([newActiveHubId]);
            } else if (isSpecialRole) {
                setSelectedInscannedHubs([]);
            } else {
                setSelectedInscannedHubs(["ALL"]);
            }
        };

        window.addEventListener("hubChanged", handleHubChanged);
        return () => window.removeEventListener("hubChanged", handleHubChanged);
    }, []);

    // Fetch hub list and cp list on mount
    useEffect(() => {
        (async () => {
            try {
                setHubListLoading(true);
                setCpListLoading(true);

                const [hubs, cps] = await Promise.all([getHubList(), getAllCPList()]);

                // Clean up hub labels if they contain the ID (e.g. "123 - Hub Name" -> "Hub Name")
                const cleanedHubs = hubs.map(hub => {
                    const id = String(hub.value);
                    const label = String(hub.label);
                    // Check if label starts with ID, followed by optional separator
                    if (label.startsWith(id)) {
                        // Remove ID and any following separator (space, hyphen, colon)
                        const newLabel = label.replace(new RegExp(`^${id}\\s*[-:]?\\s*`), "");
                        return { ...hub, label: newLabel || label };
                    }
                    return hub;
                });

                setHubList(cleanedHubs);
                setBookingCpList(cps);

                if (!hubInitializedRef.current) {
                    const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
                    const userHubs = userFullData?.userInfo?.hubDetails || [];
                    const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
                    const currentActiveHubId = activeHub?.hubId || "ALL_HUBS";
                    const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
                    const role = user?.roles?.[0];
                    setHubUser(role);

                    if (currentActiveHubId && currentActiveHubId !== "ALL_HUBS") {
                        setSelectedInscannedHubs([currentActiveHubId]);
                    } else if (userHubs?.length === 0 || role === "ROLE_HOADMIN" || role === "ROLE_CENTRALOPERATOR") {
                        setSelectedInscannedHubs([]);
                    } else {
                        setSelectedInscannedHubs(["ALL"]);
                    }
                    hubInitializedRef.current = true;
                }
            } catch {
                message.error("Failed to fetch hub or cp list");
            } finally {
                setHubListLoading(false);
                setCpListLoading(false);
                setIsInitialized(true);
            }
        })();
    }, [headerHubList]);
    // Fetch report data when filters or pagination change
    useEffect(() => {
        console.log("Effect triggered", { dateRange, selectedBookingCps, selectedInscannedHubs, awbNumbers, pagination: pagination.current, isInitialized });
        // Only fetch if date range is present and component is initialized
        if (!isInitialized) return;

        if (dateRange && dateRange[0] && dateRange[1]) {
            console.log("Fetching report via effect...");
            fetchReport();
        } else {
            console.log("Skipping fetch: Date range missing");
        }
        // eslint-disable-next-line
    }, [dateRange, selectedBookingCps, selectedInscannedHubs, awbNumbers, pagination.current, pagination.pageSize, isInitialized]);


    const fetchReport = async () => {
        console.log("fetchReport called");
        // Create new request with cancellation support
        const { signal, requestId } = createNewRequest("shipment-inscanned-vs-weight-audited");

        setLoading(true);
        try {
            const processedAwbNumbers = awbNumbers ? awbNumbers.split(",").map(val => val.trim()).filter(Boolean) : undefined;

            // Resolve Inscanned Hubs (selectedInscannedHubs) for API logic
            const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
            const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
            const resolvedInscannedHubIds = selectedInscannedHubs?.includes("ALL")
                ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedInscannedHubs;

            // Prepare params according to API requirements
            const params = {
                ...(selectedBookingCps?.length > 0 && { bookingCpIds: selectedBookingCps }),
                ...(resolvedInscannedHubIds?.length > 0 && { inscannedHubIds: resolvedInscannedHubIds }),
                ...(processedAwbNumbers && { awbNumbers: processedAwbNumbers.join(",") }),
                startDate: dateRange[0].format("YYYY-MM-DD"), // Note: API expects fromDate/toDate but generic logic might use mapped params
                endDate: dateRange[1].format("YYYY-MM-DD"),
                fromDate: dateRange[0].format("YYYY-MM-DD"),
                toDate: dateRange[1].format("YYYY-MM-DD"),
                page: pagination.current - 1,
                size: pagination.pageSize
            };

            const reportResponse = await ReportsService.getShipmentInscannedVsWeightAuditedReport(params, { signal });
            console.log("Full Report Response:", reportResponse);
            const { data, total, lastRefreshedAt, nextScheduledRefresh: nextRefresh } = reportResponse;

            // Check if this is still the latest request
            if (!isLatestRequest(requestId)) {
                console.log("🔥 Request cancelled - ignoring response");
                return;
            }

            console.log("Response Data:", data);
            setReportData(data);
            setLastsynced(lastRefreshedAt);
            setNextScheduledRefresh(nextRefresh);
            setPagination(prev => ({
                ...prev,
                total,
                // current: page + 1, // API returns data, we manage page locally or trust API
            }));
        } catch (error) {
            if (!isLatestRequest(requestId)) return;
            // Ignore cancellation errors
            if (error.name === "AbortError" || error.name === "CanceledError" || error.code === "ERR_CANCELED") {
                console.log("Fetch aborted/cancelled caught in component");
                return;
            }

            console.error("Error fetching report data:", error);
            // Show error to user if it's not a cancellation
            message.error(error.message || "Failed to fetch report data");

            setReportData([]);
            setPagination(prev => ({ ...prev, total: 0 }));
        } finally {
            if (isLatestRequest(requestId)) {
                setLoading(false);
            }
        }
    };


    const handleTableChange = (newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));
    };

    const handleDownload = async () => {
        try {
            if (!dateRange || !dateRange[0] || !dateRange[1]) {
                message.error("Please select date range for download");
                return;
            }

            const processedAwbNumbers = awbNumbers ? awbNumbers.split(",").map(val => val.trim()).filter(Boolean) : undefined;

            // Resolve Inscanned Hubs (selectedInscannedHubs) for Download logic
            const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
            const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
            const resolvedInscannedHubIds = selectedInscannedHubs?.includes("ALL")
                ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedInscannedHubs;

            const downloadParams = {
                startDate: dateRange[0].format("YYYY-MM-DD"),
                endDate: dateRange[1].format("YYYY-MM-DD"),
                bookingCpIds: selectedBookingCps.length > 0 ? selectedBookingCps : undefined,
                inscannedHubIds: resolvedInscannedHubIds?.length > 0 ? resolvedInscannedHubIds : undefined,
                awbNumbers: processedAwbNumbers
            };

            await downloadShipmentInscannedVsWeightAuditedReport(downloadParams);
            message.success("Download started successfully");
        } catch (error) {
            console.error("Download error:", error);
            message.error("Failed to start download");
        }
    };

    const columns = [
        {
            title: "No.",
            dataIndex: "index",
            key: "index",
            width: 70,
            render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
        },
        {
            title: "Tracking Id",
            dataIndex: "awbNumber",
            key: "awbNumber",
            width: 150,
            render: (text) => (
                <span
                    style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => {
                        const url = `/shipment-tracker?awb=${text}`;
                        window.open(url, "_blank");
                    }}
                >
                    {text}
                </span>
            )
        },
        { title: "Inscanned Date", dataIndex: "operationTime", key: "operationTime", width: 180, render: (text) => text ? moment(text).format("YYYY-MM-DD") : "---" },
        { title: "Booking CP", dataIndex: "bookingCp", key: "bookingCp", width: 200, render: (text) => text || "---" },
        { title: "Inscanned Hub", dataIndex: "inscannedHub", key: "inscannedHub", width: 200, render: (text) => text || "---" },
        { title: "Doc Type", dataIndex: "doctype", key: "doctype", width: 120, render: (text) => text ? text.charAt(0).toUpperCase() + text.slice(1) : "---" },
        { title: "Travel Mode", dataIndex: "travelMode", key: "travelMode", width: 120, render: (text) => text ? text.charAt(0).toUpperCase() + text.slice(1) : "---" },
        { title: "Weight Audited", dataIndex: "weightAudited", key: "weightAudited", width: 120, render: (text) => text ? text.charAt(0).toUpperCase() + text.slice(1) : "---" },
    ];

    return (
        <div className="report-container">
            <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
                <Heading
                    title="Shipment Inscanned v/s Weight Audited Adherence"
                    newReportUrl="/reports/shipment-inscan-vs-weight-audited/"
                >
                    <div style={{ paddingTop: "5px" }}>
                        <RefreshTimestamp
                            lastRefreshedAt={lastsynced}
                            nextScheduledRefresh={nextScheduledRefresh}
                        />
                    </div>
                </Heading>
            </div>
            <Card className="report-card">
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Date Range</div>
                        <CalendarComponent
                            type="range"
                            value={dateRange}
                            onChange={setDateRange}
                            format="YYYY-MM-DD"
                            allowClear
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Inscanned Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Inscanned Hub"
                            options={(hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList)}
                            onChange={(val) => {
                                const baseOptions = (hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList);
                                handleMultiSelectChange(val, setSelectedInscannedHubs, baseOptions);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedInscannedHubs}
                            allowClear
                            mode="multiple"
                            maxTagCount={1}
                            maxTagPlaceholder={(omittedValues) => `... +${omittedValues.length}`}
                            loading={hubListLoading}
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>



                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Booking CP</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Booking CP"
                            options={bookingCpList}
                            onChange={setSelectedBookingCps}
                            value={selectedBookingCps}
                            allowClear
                            mode="multiple"
                            maxTagCount={1}
                            maxTagPlaceholder={(omittedValues) => `... +${omittedValues.length}`}
                            loading={cpListLoading}
                            showSearch={true}
                            optionFilterProp="label"
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Tracking Id</div>
                        <Input
                            className="custom-input"
                            placeholder="Search Tracking Id"
                            value={awbNumbers}
                            onChange={(e) => setAwbNumbers(e.target.value)}
                            allowClear

                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">&nbsp;</div>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={handleDownload}
                            disabled={!dateRange || !dateRange[0] || !dateRange[1]}
                            style={{ width: "100%" }}
                        >
                            Download Report
                        </Button>
                    </Col>
                </Row>
            </Card>
            <Spin spinning={loading}>
                <TableComponent
                    columns={columns}
                    dataSource={reportData}
                    loading={false}
                    pagination={pagination}
                    onChange={handleTableChange}
                    scroll={{ y: "calc(100vh - 300px)", x: "max-content" }}
                    sticky
                />
            </Spin>
        </div>
    );
};

export default ShipmentInscannedVsWeightAuditedAdherenceReport;
